/**
 * Ask the backend which service's Kai should answer a question.
 *
 * Why this exists. The shell's Kai used to route on eleven keyword regexes and,
 * when none matched, fell back to the `platform` config — which points at
 * backendPort 8154, DenialManagement. So any question outside those keywords
 * became a DenialManagement question, and DM answered it approximately rather
 * than declining:
 *
 *   "What is our net collection rate?"       -> Appeal ROI
 *   "What is our clean claim rate?"          -> Operational Summary
 *   "What is our verification success rate?" -> Appeal Success Rate
 *
 * Each is a plausible percentage and none is what was asked.
 *
 * The backend classifies against every service's actual workflow registry — 118
 * intents across nine services — instead of a keyword list maintained by hand
 * here. The keyword table stays as the offline fallback: if the call fails or
 * times out, routing degrades to what it did before rather than to nothing.
 */

import { KAI_CHAT_CONFIGS } from '../constants/kaiChatConfig';
import { resolveKaiServiceFromMessage } from './platformKaiRouting';
import { createShellKaiCopilotService } from './shellKaiCopilotService';

// Routing must not add perceptible latency to sending a message: it is a
// regex pass over a static catalogue, so anything slower than this is a sick
// backend, and waiting on it is worse than using the keyword fallback.
const ROUTE_TIMEOUT_MS = 1500;

// Any RCM service can answer the routing question — they all mount the shared
// Kai router and all carry the same catalogue. PatientPortal is the exception:
// it is a different backend that does not, so route lookups from there go to
// the platform config instead.
function routingServiceKey(serviceKey) {
  return serviceKey === 'patient_portal' ? 'platform' : serviceKey;
}

// Borrow the chat client's own base rather than re-deriving it. That logic
// handles the shell proxy, the standalone frontend and direct-port access, and
// a second copy of it here would drift — routing would work in one deployment
// shape and 404 in another.
function resolveRouteUrl(serviceKey) {
  const { apiBaseUrl } = createShellKaiCopilotService(routingServiceKey(serviceKey));
  return `${apiBaseUrl}/api/v1/ai-copilot/route`;
}

/**
 * Resolve the specialist for a message.
 *
 * @param {string} message
 * @param {string} fallbackServiceKey the current page's service — used as the
 *   tiebreaker for ambiguous questions, and as the answer when nothing matches.
 * @returns {Promise<string>} a KAI_CHAT_CONFIGS key
 */
export async function resolveKaiServiceFromMessageAsync(message, fallbackServiceKey = 'platform') {
  if (!message || typeof message !== 'string') return fallbackServiceKey;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), ROUTE_TIMEOUT_MS) : null;

  try {
    const response = await fetch(resolveRouteUrl(fallbackServiceKey), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        prefer: routingServiceKey(fallbackServiceKey),
      }),
      signal: controller ? controller.signal : undefined,
    });
    if (!response.ok) throw new Error(`route failed: ${response.status}`);

    const data = await response.json();
    // A null service means no registry claimed the question with any
    // confidence. Staying put is right: the current page's Kai still has RAG
    // and general chat, and guessing is the behaviour being removed.
    if (!data || !data.service) {
      return resolveKaiServiceFromMessage(message, fallbackServiceKey);
    }
    // Membership is checked directly, NOT via getKaiChatConfig: that helper
    // returns the DenialManagement config for any unknown key, which is the
    // exact silent-misroute this router exists to remove.
    return KAI_CHAT_CONFIGS[data.service] ? data.service : fallbackServiceKey;
  } catch (error) {
    // Deliberately quiet at warn level: routing degrades, chat still works.
    console.warn('[Kai] routing lookup failed, using keyword fallback', error);
    return resolveKaiServiceFromMessage(message, fallbackServiceKey);
  } finally {
    if (timer) clearTimeout(timer);
  }
}
